import { ScrollAnimContext } from "@/contexts/ScrollAnimContext";
import { useScroll, motion, useTransform, easeInOut, cubicBezier, useMotionValueEvent } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import PictureFrame from "../PictureFrame";
import cx from "classnames";
import { twMerge } from "tailwind-merge";
import { AnimatedSectionsHeading } from "./AnimatedSectionsHeading";
import { BUN_PUBLIC_IMAGE_BASE_URL } from "@/env.json";

export default function AdventureSection() {
  const containerRef = useRef(null);
  const titleContainerRef = useRef<HTMLDivElement>(null);
  const adventureRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end end"] }); // Progress starts when the section has completely filled the viewport
  const galleryProgress = useTransform(scrollYProgress, [0, 0.4], [0, 1]); // For the main act of this scene
  const transitionOut = useTransform(scrollYProgress, [0.4, 0.9], [0, 1]);

  const images = [
    {
      src: BUN_PUBLIC_IMAGE_BASE_URL + "/adventure-1.jpg",
    },
    {
      src: BUN_PUBLIC_IMAGE_BASE_URL + "/adventure-2.jpg",
    },
    {
      src: BUN_PUBLIC_IMAGE_BASE_URL + "/adventure-3.jpg",
    },
    {
      src: BUN_PUBLIC_IMAGE_BASE_URL + "/adventure-4.jpg",
    },
    {
      src: BUN_PUBLIC_IMAGE_BASE_URL + "/adventure-5.jpg",
    },
    {
      src: BUN_PUBLIC_IMAGE_BASE_URL + "/adventure-6.jpg",
    },
  ];

  // Render picture frames (split into rows of 3)
  const pictureFrames = images.map((image, index) => {
    // Determine position and row
    const position = index % 3 === 0 ? "left" : index % 3 === 1 ? "middle" : "right";
    const row = index < 3 ? "top" : "bottom";

    // Calculate rotation and delay based on index and row
    const className = cx({
      "-rotate-15": index === 0 || index === 5,
      "rotate-15": index === 2 || index === 3,
    });

    // Calculate delay
    const delay = row === "top" ? index * 0.02 : (images.length - index) * 0.02 + 0.04;

    return (
      <PictureFrame
        key={index}
        className={twMerge("scale-85 lg:scale-100 2xl:scale-110 4xl:scale-130", className)}
        src={image.src}
        label={`Adventure ${index + 1}`}
        position={position}
        row={row}
        delay={delay}
      />
    );
  });

  //-- Title animations --//
  // Calculate offset for adventure word from center
  const [titleCenterOffset, setTitleCenterOffset] = useState(0);

  // Title styles
  const titleY = useTransform(galleryProgress, [0, 1], [60, -10]);
  const titleX = useTransform(transitionOut, [0, 0.2], [0, -titleCenterOffset], { ease: cubicBezier(0.4, 0, 0.2, 1) });
  const scale = useTransform(transitionOut, [0.25, 0.5], [1, 300], { ease: cubicBezier(0.5, 0, 0.75, 0) });
  const galleryOpacity = useTransform(transitionOut, [0, 0.5], [1, 0], { ease: cubicBezier(0.4, 0, 0.2, 1) });

  // Calculate offset of adventure word from center screen
  const calculateXoffset = () => {
    if (!adventureRef.current || !titleContainerRef.current) return;

    const adventureRect = adventureRef.current.getBoundingClientRect();
    const titleRect = titleContainerRef.current.getBoundingClientRect();
    const center = adventureRect.left + adventureRect.width / 2 - (titleRect.left + titleRect.width / 2);

    setTitleCenterOffset(center);
  };

  useEffect(() => {
    // Calculate offset of adventure word from center screen
    calculateXoffset();

    // Recalculate on window resize
    window.addEventListener("resize", calculateXoffset);
    return () => {
      window.removeEventListener("resize", calculateXoffset);
    };
  }, [adventureRef]);

  return (
    <ScrollAnimContext.Provider value={galleryProgress}>
      <motion.div
        style={{ height: "700svh", marginTop: "-250svh" }}
        className={"w-full bg-black text-neutral-100 overflow-y-clip"}
        ref={containerRef}
      >
        <div style={{ height: "100svh" }} className='sticky top-0 flex flex-col justify-center items-center w-full'>
          <AnimatedSectionsHeading className='h-full w-full flex justify-center items-center'>
            {/* "Every project is an adventure" */}
            <motion.div className='flex items-center justify-center flex-wrap' style={{ y: titleY, x: titleX }} ref={titleContainerRef}>
              <motion.h2 className='whitespace-pre' style={{ opacity: galleryOpacity }}>
                Every project is an{" "}
              </motion.h2>
              <motion.div ref={adventureRef} style={{ scale }}>
                adventure
              </motion.div>
            </motion.div>
          </AnimatedSectionsHeading>
          <motion.div className='-z-10 absolute h-full w-full py-24 flex flex-col justify-between' style={{ opacity: galleryOpacity }}>
            <div className='flex justify-around'>{pictureFrames.slice(0, 3)}</div>
            <div className='flex justify-around'>{pictureFrames.slice(3, 6)}</div>
          </motion.div>
        </div>
      </motion.div>
    </ScrollAnimContext.Provider>
  );
}
